<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

// Lista de variáveis suportadas no body_template. Hardcoded (não via i18n)
// porque vue-i18n parseia `{{paciente}}` como nested placeholder e quebra
// a compilação do template — erro "Not allowed nest placeholder" surgiu em
// 2026-05-06 quando essa string vivia em APPOINTMENTS.REMINDERS.VARIABLES_HINT.
// Os identificadores são tokens canônicos do template engine do backend, não
// texto traduzível.
const VARIABLES_HINT =
  '{{paciente}}, {{cliente}}, {{profissional}}, {{servico}}, {{data}}, {{hora}}, {{duracao}}, {{valor}}, {{empresa}}';
const MAX_BODY_LENGTH = 4096;
const MAX_OFFSET_MINUTES = 2147483647;
const MAX_DAYS_BEFORE = Math.floor(MAX_OFFSET_MINUTES / (24 * 60));
const MAX_HOURS_BEFORE = Math.floor(MAX_OFFSET_MINUTES / 60);
const WRITABLE_TEMPLATE_FIELDS = [
  'label',
  'days_before',
  'hours_before',
  'minutes_before',
  'body_template',
  'active',
  'send_via',
];

let templateUidCounter = 0;
function nextUid() {
  templateUidCounter += 1;
  return `tmpl-${templateUidCounter}`;
}

const sanitizeTemplateForPayload = template =>
  Object.fromEntries(
    WRITABLE_TEMPLATE_FIELDS.filter(field => field in template).map(field => [
      field,
      template[field],
    ])
  );

// L3: assign stable _uid to each template so :key is stable (not index)
const withStableUid = (template, previousUid) => ({
  ...template,
  tUid: template.tUid || previousUid || nextUid(),
});

const localTemplates = ref([]);

const hasWritableField = (template, field) =>
  Object.prototype.hasOwnProperty.call(template, field);
const templateMatchesWritablePayload = (template, localTemplate) =>
  WRITABLE_TEMPLATE_FIELDS.every(
    field =>
      hasWritableField(template, field) ===
        hasWritableField(localTemplate, field) &&
      template[field] === localTemplate[field]
  );
const payloadMatchesLocalTemplates = templates =>
  templates.length === localTemplates.value.length &&
  templates.every((template, index) =>
    templateMatchesWritablePayload(template, localTemplates.value[index])
  );

const isLegacyUnsupportedChannel = template =>
  template.send_via && template.send_via !== 'whatsapp';
// Ruby's String#length and OpenAPI maxLength count Unicode code points. The
// native maxlength attribute counts UTF-16 code units instead, so an emoji
// would incorrectly consume two characters in the browser.
const unicodeCodePointLength = value => Array.from(value || '').length;
const bodyLength = template => unicodeCodePointLength(template.body_template);
const fieldId = (template, field) =>
  `reminder-template-${template.tUid}-${field}`;

const emitUpdate = () => {
  emit(
    'update:modelValue',
    localTemplates.value.map(sanitizeTemplateForPayload)
  );
};

watch(
  () => props.modelValue,
  val => {
    const templates = val || [];
    // ServiceForm immediately echoes the sanitized v-model payload. Keeping
    // the local objects for that echo preserves each control's DOM identity,
    // keyboard focus and cursor position while the user types.
    if (payloadMatchesLocalTemplates(templates)) return;

    const uidByPersistedId = new Map(
      localTemplates.value
        .filter(template => template.id != null)
        .map(template => [template.id, template.tUid])
    );
    localTemplates.value = templates.map(template =>
      withStableUid(template, uidByPersistedId.get(template.id))
    );
  },
  { immediate: true }
);

const addTemplate = () => {
  localTemplates.value.push({
    tUid: nextUid(),
    label: '',
    days_before: 0,
    hours_before: 1,
    minutes_before: 0,
    body_template: '',
    active: true,
    send_via: 'whatsapp',
  });
  emitUpdate();
};

const removeTemplate = idx => {
  localTemplates.value.splice(idx, 1);
  emitUpdate();
};

const updateTemplate = (idx, field, value) => {
  localTemplates.value[idx] = { ...localTemplates.value[idx], [field]: value };
  emitUpdate();
};
</script>

<template>
  <div class="space-y-4">
    <div
      v-for="(template, idx) in localTemplates"
      :key="template.tUid"
      class="border border-n-weak rounded-lg p-4 space-y-3"
    >
      <!-- L-06: inline label field (replaces hardcoded "Reminder N") -->
      <div class="flex items-center justify-between gap-3">
        <label :for="fieldId(template, 'label')" class="sr-only">
          {{ t('APPOINTMENTS.REMINDERS.LABEL') }}
        </label>
        <input
          :id="fieldId(template, 'label')"
          :name="fieldId(template, 'label')"
          :value="template.label"
          type="text"
          autocomplete="off"
          :placeholder="t('APPOINTMENTS.REMINDERS.LABEL_PLACEHOLDER')"
          class="min-h-11 flex-1 rounded-sm border-0 bg-transparent px-0 text-sm font-semibold text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
          @input="updateTemplate(idx, 'label', $event.target.value)"
        />
        <div class="flex items-center gap-3 shrink-0">
          <div class="flex min-h-11 items-center gap-1.5">
            <input
              :id="fieldId(template, 'active')"
              :name="fieldId(template, 'active')"
              type="checkbox"
              :checked="template.active"
              class="rounded border-n-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
              @change="updateTemplate(idx, 'active', $event.target.checked)"
            />
            <label
              :for="fieldId(template, 'active')"
              class="text-sm text-n-slate-11"
            >
              {{ t('APPOINTMENTS.REMINDERS.ACTIVE') }}
            </label>
          </div>
          <button
            type="button"
            data-testid="remove-template"
            class="min-h-11 touch-manipulation rounded px-2 text-sm font-medium text-n-ruby-9 hover:text-n-ruby-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
            @click="removeTemplate(idx)"
          >
            {{ t('APPOINTMENTS.TABLE.DELETE') }}
          </button>
        </div>
      </div>

      <div
        v-if="isLegacyUnsupportedChannel(template)"
        role="alert"
        data-testid="unsupported-channel-warning"
        class="flex items-start gap-2 rounded-lg border border-n-amber-7 bg-n-amber-3 px-3 py-2 text-sm text-n-amber-12"
      >
        <span
          class="i-lucide-triangle-alert mt-0.5 size-4 shrink-0"
          aria-hidden="true"
        />
        <span>{{
          t('APPOINTMENTS.REMINDERS.LEGACY_CHANNEL_UNSUPPORTED', {
            channel: template.send_via,
          })
        }}</span>
      </div>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label
            :for="fieldId(template, 'days-before')"
            class="block text-xs font-medium text-n-slate-11 mb-1"
          >
            {{ t('APPOINTMENTS.REMINDERS.DAYS_BEFORE') }}
          </label>
          <input
            :id="fieldId(template, 'days-before')"
            :name="fieldId(template, 'days-before')"
            type="number"
            inputmode="numeric"
            autocomplete="off"
            step="1"
            min="0"
            :max="MAX_DAYS_BEFORE"
            :value="template.days_before"
            class="min-h-11 w-full rounded border border-n-strong px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
            @input="
              updateTemplate(idx, 'days_before', Number($event.target.value))
            "
          />
        </div>
        <div>
          <label
            :for="fieldId(template, 'hours-before')"
            class="block text-xs font-medium text-n-slate-11 mb-1"
          >
            {{ t('APPOINTMENTS.REMINDERS.HOURS_BEFORE') }}
          </label>
          <input
            :id="fieldId(template, 'hours-before')"
            :name="fieldId(template, 'hours-before')"
            type="number"
            inputmode="numeric"
            autocomplete="off"
            step="1"
            min="0"
            :max="MAX_HOURS_BEFORE"
            :value="template.hours_before"
            class="min-h-11 w-full rounded border border-n-strong px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
            @input="
              updateTemplate(idx, 'hours_before', Number($event.target.value))
            "
          />
        </div>
        <div>
          <label
            :for="fieldId(template, 'minutes-before')"
            class="block text-xs font-medium text-n-slate-11 mb-1"
          >
            {{ t('APPOINTMENTS.REMINDERS.MINUTES_BEFORE') }}
          </label>
          <input
            :id="fieldId(template, 'minutes-before')"
            :name="fieldId(template, 'minutes-before')"
            type="number"
            inputmode="numeric"
            autocomplete="off"
            step="1"
            min="0"
            :max="MAX_OFFSET_MINUTES"
            :value="template.minutes_before"
            class="min-h-11 w-full rounded border border-n-strong px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
            @input="
              updateTemplate(idx, 'minutes_before', Number($event.target.value))
            "
          />
        </div>
      </div>

      <div>
        <label
          :for="fieldId(template, 'body')"
          class="block text-xs font-medium text-n-slate-11 mb-1"
        >
          {{ t('APPOINTMENTS.REMINDERS.BODY') }}
        </label>

        <!-- L-08: Variables hint above body, styled blue callout -->
        <div
          :id="fieldId(template, 'body-hint')"
          class="rounded-md bg-n-blue-3 border border-n-blue-6 px-3 py-2 mb-2"
        >
          <p class="text-xs text-n-blue-12">
            <strong>{{
              t('APPOINTMENTS.REMINDERS.VARIABLES_LABEL_COLON')
            }}</strong>
            <code class="font-mono text-xs ml-1">{{ VARIABLES_HINT }}</code>
          </p>
        </div>

        <textarea
          :id="fieldId(template, 'body')"
          :name="fieldId(template, 'body')"
          :value="template.body_template"
          autocomplete="off"
          :aria-invalid="bodyLength(template) > MAX_BODY_LENGTH"
          :aria-describedby="`${fieldId(template, 'body-hint')} ${fieldId(template, 'body-counter')}`"
          rows="3"
          class="w-full resize-none rounded border border-n-strong px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
          @input="updateTemplate(idx, 'body_template', $event.target.value)"
        />
        <div class="mt-1 flex items-start justify-between gap-3 text-xs">
          <span
            v-if="bodyLength(template) > MAX_BODY_LENGTH"
            role="alert"
            class="text-n-ruby-11"
          >
            {{ t('APPOINTMENTS.REMINDERS.BODY_TOO_LONG') }}
          </span>
          <span v-else />
          <span
            :id="fieldId(template, 'body-counter')"
            data-testid="body-length-counter"
            class="shrink-0 tabular-nums text-n-slate-10"
          >
            {{
              t('APPOINTMENTS.REMINDERS.BODY_LENGTH', {
                current: bodyLength(template),
                max: MAX_BODY_LENGTH,
              })
            }}
          </span>
        </div>
      </div>
    </div>

    <button
      type="button"
      class="inline-flex min-h-11 touch-manipulation items-center gap-1.5 rounded px-2 text-sm font-medium text-n-brand hover:text-n-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2"
      @click="addTemplate"
    >
      {{ t('APPOINTMENTS.REMINDERS.ADD') }}
    </button>
  </div>
</template>
